import {
  CanvasNode,
  CanvasGroup,
  CanvasEdge,
  CanvasSection,
  CanvasStandaloneNode,
  CanvasElementType,
} from '../sweep-canvas/canvas-types';
import { convertGridPositionToXYPosition } from '../sweep-canvas/helpers/gridPositioningUtils';
import { getObjectTypeColor } from '../sweep-canvas/helpers/getObjectTypeColor';
import { NODE_WIDTH, NODE_HEIGHT, SECTION_NODE_PADDING } from '../sweep-canvas/const';
import type { PreviewNode, PreviewEdge, PreviewSection, PreviewData, BoundingBox } from './types';

const PADDING = 20;

interface NormalizeInput {
  sweepNodes: CanvasNode[];
  sweepGroups: CanvasGroup[];
  sweepEdges: CanvasEdge[];
  sweepSections: CanvasSection[];
  sweepStandaloneNodes: CanvasStandaloneNode[];
}

const calculateBoundingBox = (nodes: PreviewNode[], sections: PreviewSection[]): BoundingBox => {
  if (nodes.length === 0 && sections.length === 0) {
    return { minX: 0, minY: 0, maxX: 100, maxY: 100, width: 100, height: 100 };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const node of nodes) {
    minX = Math.min(minX, node.x);
    minY = Math.min(minY, node.y);
    maxX = Math.max(maxX, node.x + node.width);
    maxY = Math.max(maxY, node.y + node.height);
  }

  for (const section of sections) {
    minX = Math.min(minX, section.x);
    minY = Math.min(minY, section.y);
    maxX = Math.max(maxX, section.x + section.width);
    maxY = Math.max(maxY, section.y + section.height);
  }

  return {
    minX: minX - PADDING,
    minY: minY - PADDING,
    maxX: maxX + PADDING,
    maxY: maxY + PADDING,
    width: maxX - minX + PADDING * 2,
    height: maxY - minY + PADDING * 2,
  };
};

const getSectionOffset = (
  sectionId: string | undefined,
  sections: CanvasSection[],
): { x: number; y: number } => {
  if (!sectionId) return { x: 0, y: 0 };

  const section = sections.find((s) => s.id === sectionId);
  if (!section) return { x: 0, y: 0 };

  return {
    x: section.xyPosition.x + SECTION_NODE_PADDING,
    y: section.xyPosition.y + SECTION_NODE_PADDING,
  };
};

export const normalizeForPreviewData = (input: NormalizeInput): PreviewData => {
  const { sweepNodes, sweepGroups, sweepEdges, sweepSections, sweepStandaloneNodes } = input;

  const previewNodes: PreviewNode[] = [];
  const previewEdges: PreviewEdge[] = [];
  const previewSections: PreviewSection[] = [];

  const nodePositionMap = new Map<
    string,
    { x: number; y: number; width: number; height: number; objectType?: string }
  >();

  for (const section of sweepSections) {
    const previewSection: PreviewSection = {
      id: section.id,
      x: section.xyPosition.x,
      y: section.xyPosition.y,
      width: section.width,
      height: section.height,
      color: section.color,
      name: section.name,
    };
    previewSections.push(previewSection);
  }

  for (const group of sweepGroups) {
    const objectType = group.objectType;
    const colors = getObjectTypeColor(objectType);
    const sectionOffset = getSectionOffset(group.sectionId, sweepSections);
    const groupAnchor = convertGridPositionToXYPosition(group.position, {
      withPaddingByNodeType: CanvasElementType.GROUP,
    });

    const groupSteps = sweepNodes.filter((node) => node.parentId === group.id);
    const stepPositions: Array<{ id: string; x: number; y: number }> = [];

    for (const step of groupSteps) {
      const stepRelative = convertGridPositionToXYPosition(step.position, {
        withPaddingByNodeType: CanvasElementType.REGULAR,
      });

      const stepX = groupAnchor.x + sectionOffset.x + stepRelative.x;
      const stepY = groupAnchor.y + sectionOffset.y + stepRelative.y;

      stepPositions.push({ id: step.id, x: stepX, y: stepY });

      const stepNode: PreviewNode = {
        id: step.id,
        type: 'step',
        parentId: group.id,
        x: stepX,
        y: stepY,
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        color: colors.step,
        objectType,
      };
      previewNodes.push(stepNode);

      nodePositionMap.set(step.id, {
        x: stepX + NODE_WIDTH / 2,
        y: stepY + NODE_HEIGHT / 2,
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        objectType,
      });
    }

    const GROUP_PADDING_X = 20;
    const GROUP_PADDING_TOP = 60;
    const GROUP_PADDING_BOTTOM = 20;

    let groupMinX = Infinity,
      groupMinY = Infinity,
      groupMaxX = -Infinity,
      groupMaxY = -Infinity;

    for (const pos of stepPositions) {
      groupMinX = Math.min(groupMinX, pos.x);
      groupMinY = Math.min(groupMinY, pos.y);
      groupMaxX = Math.max(groupMaxX, pos.x + NODE_WIDTH);
      groupMaxY = Math.max(groupMaxY, pos.y + NODE_HEIGHT);
    }

    if (stepPositions.length === 0) {
      groupMinX = groupAnchor.x + sectionOffset.x;
      groupMinY = groupAnchor.y + sectionOffset.y;
      groupMaxX = groupMinX + NODE_WIDTH;
      groupMaxY = groupMinY + NODE_HEIGHT;
    }

    const groupVisualX = groupMinX - GROUP_PADDING_X;
    const groupVisualY = groupMinY - GROUP_PADDING_TOP;
    const groupVisualWidth = groupMaxX - groupMinX + GROUP_PADDING_X * 2;
    const groupVisualHeight = groupMaxY - groupMinY + GROUP_PADDING_TOP + GROUP_PADDING_BOTTOM;

    const groupNode: PreviewNode = {
      id: group.id,
      type: 'group',
      x: groupVisualX,
      y: groupVisualY,
      width: groupVisualWidth,
      height: groupVisualHeight,
      color: colors.connection + '30',
      objectType,
    };
    previewNodes.push(groupNode);

    nodePositionMap.set(`label-${group.id}`, {
      x: groupVisualX + groupVisualWidth / 2,
      y: groupVisualY,
      width: groupVisualWidth,
      height: groupVisualHeight,
      objectType,
    });
    nodePositionMap.set(group.id, {
      x: groupVisualX,
      y: groupVisualY,
      width: groupVisualWidth,
      height: groupVisualHeight,
      objectType,
    });
  }

  const NAME_CHAR_WIDTH = 10;
  const TYPE_CHAR_WIDTH = 7.5;
  const MIN_STANDALONE_WIDTH = NODE_WIDTH;
  const STANDALONE_H_PADDING = 32;

  for (const standalone of sweepStandaloneNodes) {
    const sectionOffset = getSectionOffset(standalone.sectionId, sweepSections);
    const standaloneXY = convertGridPositionToXYPosition(standalone.position, {
      withPaddingByNodeType: CanvasElementType.STANDALONE_NODE,
    });

    const nameWidth = standalone.name.length * NAME_CHAR_WIDTH + STANDALONE_H_PADDING;
    const typeWidth = standalone.type.length * TYPE_CHAR_WIDTH + STANDALONE_H_PADDING;
    const calculatedWidth = Math.max(nameWidth, typeWidth);
    const standaloneWidth = Math.max(MIN_STANDALONE_WIDTH, calculatedWidth);

    const standaloneNode: PreviewNode = {
      id: standalone.id,
      type: 'standalone',
      x: standaloneXY.x + sectionOffset.x,
      y: standaloneXY.y + sectionOffset.y,
      width: standaloneWidth,
      height: NODE_HEIGHT,
      color: standalone.color || '#FFFFFF',
      label: standalone.name,
      sublabel: standalone.type,
    };
    previewNodes.push(standaloneNode);

    nodePositionMap.set(standalone.id, {
      x: standaloneXY.x + sectionOffset.x + standaloneWidth / 2,
      y: standaloneXY.y + sectionOffset.y + NODE_HEIGHT / 2,
      width: standaloneWidth,
      height: NODE_HEIGHT,
    });
  }

  for (const edge of sweepEdges) {
    const sourcePos = nodePositionMap.get(edge.source);
    const targetPos = nodePositionMap.get(edge.target);

    if (!sourcePos || !targetPos) {
      continue;
    }

    // Find source and target nodes to determine edge type
    const sourceNode = previewNodes.find((n) => n.id === edge.source);
    const targetNode = previewNodes.find((n) => n.id === edge.target);

    // Filter logic:
    // - Include: standalone to anything, step-to-step cross-funnel
    // - Exclude: step to step within same funnel (exit criteria), funnel-to-funnel, step-to-funnel, funnel-to-step

    const isStepToStep = sourceNode?.type === 'step' && targetNode?.type === 'step';
    const isSameFunnel = sourceNode?.parentId && sourceNode.parentId === targetNode?.parentId;

    // Skip step-to-step edges within the same funnel
    if (isStepToStep && isSameFunnel) {
      continue;
    }

    const targetIsGroupLabel = edge.target.startsWith('label-');
    const sourceIsStandalone = sourceNode?.type === 'standalone';
    const sourceIsStep = sourceNode?.type === 'step';
    const targetIsStep = targetNode?.type === 'step';

    // Exclude:
    // - Funnel-to-funnel (group → group label): excluded by removing targetIsGroupLabel condition
    // - Step-to-funnel (step → group label): excluded by removing targetIsGroupLabel condition
    // - Funnel-to-step (group → step): excluded because sourceIsStep must be true for step-to-step edges

    // Include edge if:
    // - Source is standalone (metadata) connecting to anything
    // - Step-to-step cross-funnel (already filtered by isSameFunnel check above)
    const shouldInclude =
      sourceIsStandalone || (sourceIsStep && targetIsStep && !isSameFunnel);

    if (!shouldInclude) {
      continue;
    }

    const sourceColors = getObjectTypeColor(sourcePos.objectType);
    const targetColors = getObjectTypeColor(targetPos.objectType);

    const previewEdge: PreviewEdge = {
      id: edge.id,
      sourceId: edge.source,
      targetId: edge.target,
      sourceColor: sourceColors.connection,
      targetColor: targetColors.connection,
      type: targetIsGroupLabel ? 'group' : 'step',
    };
    previewEdges.push(previewEdge);
  }

  // Calculate bounding box
  const boundingBox = calculateBoundingBox(previewNodes, previewSections);

  return {
    nodes: previewNodes,
    edges: previewEdges,
    sections: previewSections,
    boundingBox,
  };
};
